import { useQuery } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api-client";
import type { CertDownloadUrlData } from "@/hooks/queries/admin-legacy-cert-types";

// GET /admin/legacy-certs/{id}/download (Story 16.4, AC10) — a fresh short-TTL
// pre-signed S3 GET URL the Review panel renders inline (PDF iframe / image). Modelled
// as a query (not a mutation like the Resources download): the panel needs the URL on
// open to PREVIEW the cert, and a cert view is NOT an audited PHI access (unlike the
// Resources download, which writes an audit row and must be 1:1 with a click). `enabled`
// is caller-controlled so the URL is minted only when a cert is being reviewed.
//
// staleTime 0 + the per-cert key mean a re-open re-mints (the prior URL may have
// expired); never persisted beyond the panel.

/** Key prefix for the per-cert download URL — append the certId for the full key.
 * Exported so a future consumer can invalidate without duplicating the string. */
export const CERT_DOWNLOAD_URL_QUERY_KEY_PREFIX = [
  "admin",
  "legacy-certs",
  "download",
] as const;

export function useLegacyCertDownloadUrl(certId: string | null) {
  return useQuery({
    queryKey: [...CERT_DOWNLOAD_URL_QUERY_KEY_PREFIX, certId],
    queryFn: () =>
      apiFetch<CertDownloadUrlData>(`/admin/legacy-certs/${certId}/download`),
    enabled: certId != null,
    staleTime: 0,
    gcTime: 0,
  });
}
